// One definition of "a customer address as staff type it", shared by the company page's Addresses card,
// the New Company window and the job page's "+ Create" window, so the three can never drift apart again
// (they used to each have their own copy -- and the job-page one wrote the OLD customers columns).
// Stored in customer_addresses (never in the legacy customers.address_line_* columns).

// CDS Names & Addresses v1.7 field-length limits. Address lines are dynamic: the 35-char cap is an input maxlength only;
// the hard CDS STREET truncation happens at export time, not on storage.
export const ADDR_CDS_LIMITS = { addressLine: 35, city: 35, postcode: 9 } as const
export const MAX_ADDRESS_LINES = 6

export const ADDR_TYPE_META: Record<string, { label: string; color: string }> = {
  operations: { label: 'Operations', color: 'bg-blue-100 text-blue-700' },
  invoice:    { label: 'Invoice',    color: 'bg-purple-100 text-purple-700' },
  registered: { label: 'Registered', color: 'bg-green-100 text-green-700' },
  delivery:   { label: 'Delivery',   color: 'bg-orange-100 text-orange-700' },
  collection: { label: 'Collection', color: 'bg-cyan-100 text-cyan-700' },
  returns:    { label: 'Returns',    color: 'bg-yellow-100 text-yellow-700' },
  other:      { label: 'Other',      color: 'bg-gray-100 text-gray-700' },
}
export const ALL_ADDR_TYPES = ['operations', 'invoice', 'registered', 'delivery', 'collection', 'returns', 'other']
// Same order the database uses for the legacy address_type column (public.address_role_rank).
export const roleRank = (r: string): number => { const i = ALL_ADDR_TYPES.indexOf(r); return i < 0 ? 99 : i }
export const sortRoles = (roles: string[]): string[] => [...new Set(roles)].sort((a, b) => roleRank(a) - roleRank(b))

// ONE address, SEVERAL roles: `roles` = everything it is used as, `primary_roles` = the roles for which it is THE address of the
// company (at most one primary per role -- ticking it here quietly replaces the previous holder).
export interface AddressDraft {
  roles: string[]
  primary_roles: string[]
  label: string
  address_lines: string[]
  city: string
  county: string
  postcode: string
  country: string
  contact_name: string
  contact_phone: string
  contact_email: string
  notes: string
}

// Rows read back from the database carry null for empty columns; the form treats null like empty.
export type AddressFieldsValue = { [K in keyof AddressDraft]?: AddressDraft[K] | null }

export const emptyAddressDraft = (type = 'operations'): AddressDraft => ({
  roles: [type], primary_roles: [type], label: '',
  address_lines: [''], city: '', county: '', postcode: '', country: 'GB',
  contact_name: '', contact_phone: '', contact_email: '', notes: '',
})

// "Nothing typed": the country defaults to GB and the roles to Operations, so those never count as input.
export function isAddressDraftBlank(d: AddressFieldsValue): boolean {
  const lines = (d.address_lines ?? []).some(l => l.trim())
  return !(lines || (d.city ?? '').trim() || (d.county ?? '').trim() || (d.postcode ?? '').trim()
    || (d.contact_name ?? '').trim() || (d.contact_phone ?? '').trim() || (d.contact_email ?? '').trim())
}

// Normalised "same place" key: street lines + postcode, letters/digits only. Used to stop the same premises being typed twice.
export const addressSameKey = (lines: string[] | null | undefined, postcode: string | null | undefined): string =>
  (lines ?? []).join('').toUpperCase().replace(/[^A-Z0-9]/g, '') + '|' + (postcode ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '')

// Returns an error message, or null when the draft may be saved. Same limits the company page enforced, plus: an address
// with no street line is not an address (it is what produced the "UNITED KINGDOM, UNITED KINGDOM" rows).
export function validateAddressDraft(d: AddressFieldsValue): string | null {
  const lines = (d.address_lines ?? []).map(l => l.trim()).filter(Boolean)
  if (lines.length === 0) return 'Enter at least one address line (street) -- or clear the address section to add it later.'
  if (!/[A-Za-z]/.test(lines.join(' '))) return 'The address line must contain a street name, not only numbers.'
  if ((d.roles ?? []).length === 0) return 'Tick at least one use for this address (Operations, Invoice, Registered...).'
  if ((d.city ?? '').length > ADDR_CDS_LIMITS.city) return `City exceeds ${ADDR_CDS_LIMITS.city} characters (CDS limit).`
  if ((d.postcode ?? '').length > ADDR_CDS_LIMITS.postcode) return `Postcode exceeds ${ADDR_CDS_LIMITS.postcode} characters (CDS limit).`
  if (d.country && d.country.length !== 2) return 'Country must be a 2-letter ISO code (CDS limit).'
  return null
}

// The row to insert into customer_addresses. `primaryForAllRoles` = this is the company's first address: it is the primary for
// every role it has. (address_type / is_default are mirrored by the database from roles / primary_roles.)
export function addressDraftToRow(d: AddressFieldsValue, customerId: string, primaryForAllRoles: boolean) {
  const roles = sortRoles(d.roles && d.roles.length > 0 ? d.roles : ['operations'])
  const primary = primaryForAllRoles ? roles : sortRoles((d.primary_roles ?? []).filter(r => roles.includes(r)))
  return {
    customer_id: customerId,
    roles,
    primary_roles: primary,
    address_type: roles[0],
    label: d.label || null,
    address_lines: (d.address_lines ?? []).map(l => l.trim()).filter(Boolean),
    city: d.city || null,
    county: d.county || null,
    postcode: d.postcode || null,
    country: d.country || 'GB',
    contact_name: d.contact_name || null,
    contact_phone: d.contact_phone || null,
    contact_email: d.contact_email || null,
    notes: d.notes || null,
  }
}
